import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import {
  Bell,
  BellOff,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
  User,
} from "lucide-react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import validator from "validator";
import PhoneInput from "react-phone-input-2";
import sh from "bundles/common/utils/sh";
import { replaceCurrentCandidate } from "bundles/Candidate/constants/candidateConstants";
import { countryMap } from "bundles/common/utils/constants";

const cardSx = {
  bgcolor: "hsl(0, 0%, 100%)",
  borderRadius: "16px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  overflow: "hidden",
};

const sectionTitleSx = {
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  fontSize: "15px",
  color: "hsl(178, 93%, 10%)",
};

const sectionDescSx = {
  fontSize: "12px",
  color: "hsl(178, 10%, 45%)",
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
};

const labelSx = {
  fontSize: "11px",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  color: "hsl(178, 10%, 45%)",
  mb: "6px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    fontSize: "13px",
    borderRadius: "10px",
    bgcolor: "hsla(160, 10%, 96%, 0.3)",
    "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
    "&:hover fieldset": { borderColor: "hsla(160, 10%, 80%, 0.8)" },
    "&.Mui-focused fieldset": { borderColor: "hsl(153, 68%, 25%)", borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    fontSize: "13px",
    color: "hsl(178, 10%, 45%)",
    "&.Mui-focused": { color: "hsl(153, 68%, 25%)" },
  },
};

const btnPrimarySx = {
  component: "button",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  py: "10px",
  px: "24px",
  borderRadius: "10px",
  border: "none",
  cursor: "pointer",
  bgcolor: "hsl(153, 68%, 25%)",
  color: "hsl(0, 0%, 100%)",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  fontSize: "13px",
  transition: "background-color 0.15s",
  "&:hover:not(:disabled)": { bgcolor: "hsl(153, 68%, 30%)" },
  "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
};

const btnDangerSx = {
  component: "button",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  py: "8px",
  px: "16px",
  borderRadius: "10px",
  border: "1px solid hsla(0, 65%, 51%, 0.25)",
  cursor: "pointer",
  bgcolor: "transparent",
  color: "hsl(0, 65%, 51%)",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  fontSize: "12px",
  transition: "all 0.15s",
  "&:hover": { bgcolor: "hsla(0, 65%, 51%, 0.06)" },
};

const ToggleRow = ({ label, checked, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: "12px",
      px: "16px",
      borderRadius: "12px",
      border: "1px solid hsla(160, 10%, 90%, 0.6)",
      bgcolor: "hsla(160, 10%, 96%, 0.15)",
      transition: "border-color 0.15s",
      "&:hover": { borderColor: "hsla(160, 10%, 80%, 0.8)" },
    }}
  >
    <Typography
      sx={{
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
        fontSize: "13px",
        color: "hsl(178, 93%, 10%)",
      }}
    >
      {label}
    </Typography>
    <Box
      component="button"
      onClick={onChange}
      sx={{
        position: "relative",
        width: 40,
        height: 22,
        borderRadius: "9999px",
        border: "none",
        cursor: "pointer",
        bgcolor: checked ? "hsl(153, 68%, 25%)" : "hsla(160, 10%, 80%, 0.6)",
        transition: "background-color 0.2s",
        flexShrink: 0,
        p: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "2px",
          left: checked ? "20px" : "2px",
          width: 18,
          height: 18,
          borderRadius: "9999px",
          bgcolor: "hsl(0, 0%, 100%)",
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
        }}
      />
    </Box>
  </Box>
);

const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const Settings = () => {
  const currentCandidate = useSelector((state) => state.current_candidate);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const intl = useIntl();
  const query = useQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [profileValues, setProfileValues] = useState({
    first_name: currentCandidate?.first_name || "",
    last_name: currentCandidate?.last_name || "",
    email: currentCandidate?.email || "",
    phone: currentCandidate?.phone || "",
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);

  const [notifications, setNotifications] = useState(
    currentCandidate?.notification_preferences || {}
  );
  const [notifLoading, setNotifLoading] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [blacklistedCompanies, setBlacklistedCompanies] = useState(
    currentCandidate?.blacklisted_companies || []
  );
  const [privacyLoading, setPrivacyLoading] = useState(false);

  const [passwordValues, setPasswordValues] = useState({
    currentPassword: "",
    password: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    if (!currentCandidate) {
      navigate({ pathname: `/${i18nLocale}/candidate/job_offers` });
    }
  }, [currentCandidate]);

  useEffect(() => {
    sh.get(`${i18nLocale}/company`, {}).then((res) => {
      setCompanies(res?.data || []);
    });
  }, []);

  useEffect(() => {
    if (query.get("unsubscribe") === "true") {
      const hasActive = Object.values(notifications).some((v) => v === true);
      const emailDeliveryId = query.get("email_delivery_id");
      if (hasActive) unsubscribeAll(emailDeliveryId);
      navigate({ search: "" });
    }
  }, []);

  const handleProfileInput = (e) => {
    const { name, value } = e.target;
    setProfileValues((prev) => ({ ...prev, [name]: value }));
    if (!value) {
      setProfileErrors((prev) => ({ ...prev, [name]: intl.formatMessage({ id: "form_validation.required" }) }));
    } else {
      setProfileErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneChange = (value) => {
    setProfileValues((prev) => ({ ...prev, phone: value }));
  };

  const saveProfile = () => {
    const required = intl.formatMessage({ id: "form_validation.required" });
    const errs = {};
    if (!profileValues.first_name) errs.first_name = required;
    if (!profileValues.last_name) errs.last_name = required;
    if (!profileValues.email) errs.email = required;
    setProfileErrors(errs);
    if (Object.values(errs).some((v) => v)) return;

    setProfileLoading(true);
    sh.put("candidate", {
      authenticity_token: authenticityToken,
      candidate: { ...profileValues, id: currentCandidate.id },
    })
      .then((res) => {
        replaceCurrentCandidate(dispatch, authenticityToken, () => {});
        enqueueSnackbar(
          intl.formatMessage(
            { id: "candidate.updated_profile" },
            { percentage: res?.data?.profile_completion }
          ),
          { variant: "success" }
        );
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ id: "failed_snack" }), { variant: "error" });
      })
      .finally(() => setProfileLoading(false));
  };

  const toggleNotif = (type) => {
    setNotifications((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const saveNotifications = (overrideNotifs = notifications) => {
    setNotifLoading(true);
    sh.put("candidate", {
      authenticity_token: authenticityToken,
      candidate: {
        notification_preferences: overrideNotifs,
        id: currentCandidate.id,
      },
    })
      .then(() => {
        replaceCurrentCandidate(dispatch, authenticityToken, () => {});
        enqueueSnackbar(
          intl.formatMessage({ id: "candidate.updated_notifications_preferences" }),
          { variant: "success" }
        );
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage({ id: "failed_snack" }), { variant: "error" });
      })
      .finally(() => setNotifLoading(false));
  };

  const unsubscribeAll = (emailDeliveryId = null) => {
    const allOff = {};
    Object.keys(notifications).forEach((k) => (allOff[k] = false));
    setNotifications(allOff);
    saveNotifications(allOff);
    if (emailDeliveryId) {
      sh.put(`/email-deliveries/mark-as-unsubscribed/${emailDeliveryId}`, {
        authenticity_token: authenticityToken,
      });
    }
  };

  const savePrivacy = () => {
    setPrivacyLoading(true);
    sh.put("candidate", {
      authenticity_token: authenticityToken,
      id: currentCandidate.id,
      candidate: {
        blacklisted_company_ids: blacklistedCompanies.map((c) => c.id),
      },
    })
      .then(() => {
        replaceCurrentCandidate(dispatch, authenticityToken, () => {});
        enqueueSnackbar(
          intl.formatMessage({ id: "settings.save_success" }),
          { variant: "success" }
        );
      })
      .catch((err) => console.error(err))
      .finally(() => setPrivacyLoading(false));
  };

  const handlePwInput = (e) => {
    const { name, value } = e.target;
    setPasswordValues((prev) => ({ ...prev, [name]: value }));
    validatePassword({ [name]: value });
  };

  const validatePassword = (fields = passwordValues) => {
    const temp = { ...passwordErrors };
    const required = intl.formatMessage({ id: "form_validation.required" });
    const notStrong = intl.formatMessage({ id: "form_validation.not_a_strong_password" });

    if ("currentPassword" in fields && !currentCandidate?.password_generated) {
      temp.currentPassword = fields.currentPassword ? "" : required;
    }
    if ("password" in fields) {
      temp.password = fields.password ? "" : required;
      if (fields.password && !validator.isStrongPassword(fields.password)) {
        temp.password = notStrong;
      } else if (fields.password) {
        temp.password = "";
      }
    }
    setPasswordErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!validatePassword(passwordValues)) return;
    setPasswordLoading(true);
    sh.put(`/${i18nLocale}/candidate/update_password`, {
      authenticity_token: authenticityToken,
      candidate: {
        id: currentCandidate?.id,
        current_password: passwordValues.currentPassword,
        password: passwordValues.password,
      },
    })
      .then(() => {
        replaceCurrentCandidate(dispatch, authenticityToken, () => {});
        enqueueSnackbar(
          intl.formatMessage({ id: "devise.password.update_password_success" }),
          { variant: "success" }
        );
        setPasswordValues({ currentPassword: "", password: "" });
      })
      .catch((err) => {
        if (err?.response?.data?.current_password) {
          enqueueSnackbar(
            intl.formatMessage({ id: "devise.password.invalid_password" }),
            { variant: "error" }
          );
        } else {
          enqueueSnackbar(intl.formatMessage({ id: "flash.unknown" }), {
            variant: "error",
          });
        }
      })
      .finally(() => setPasswordLoading(false));
  };

  if (!currentCandidate) return null;


  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "hsla(160, 10%, 96%, 0.3)",
        py: { xs: "16px", md: "40px" },
        px: { xs: "12px", md: "24px" },
      }}
    >
      <Box sx={{ maxWidth: "640px", mx: "auto" }}>
        {/* ── Page header ── */}
        <Box sx={{ mb: "28px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "6px" }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                bgcolor: "hsla(153, 68%, 25%, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SettingsIcon size={18} color="hsl(153, 68%, 25%)" />
            </Box>
            <Typography
              sx={{
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: { xs: "22px", md: "26px" },
                color: "hsl(178, 93%, 10%)",
              }}
            >
              <FormattedMessage id="settings.title" />
            </Typography>
          </Box>
          <Typography sx={{ ...sectionDescSx, ml: "46px" }}>
            <FormattedMessage id="settings.subtitle" />
          </Typography>
        </Box>

        {/* Profile */}
        <Box sx={{ ...cardSx, p: { xs: "20px", md: "28px" }, mb: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "20px" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: "hsla(153, 68%, 25%, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <User size={18} color="hsl(153, 68%, 25%)" />
            </Box>
            <Box>
              <Typography sx={sectionTitleSx}>
                <FormattedMessage id="settings.general_info" />
              </Typography>
              <Typography sx={sectionDescSx}>
                Vos informations de contact principales.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: "14px",
              mb: "20px",
            }}
          >
            <Box>
              <Typography sx={labelSx}>
                <FormattedMessage id="devise.signup.first_name" />
              </Typography>
              <TextField
                name="first_name"
                value={profileValues.first_name}
                onChange={handleProfileInput}
                error={!!profileErrors.first_name}
                helperText={profileErrors.first_name || ""}
                fullWidth
                size="small"
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography sx={labelSx}>
                <FormattedMessage id="devise.signup.last_name" />
              </Typography>
              <TextField
                name="last_name"
                value={profileValues.last_name}
                onChange={handleProfileInput}
                error={!!profileErrors.last_name}
                helperText={profileErrors.last_name || ""}
                fullWidth
                size="small"
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography sx={labelSx}>
                <FormattedMessage id="devise.signup.email" />
              </Typography>
              <TextField
                name="email"
                value={profileValues.email}
                onChange={handleProfileInput}
                error={!!profileErrors.email}
                helperText={profileErrors.email || ""}
                fullWidth
                size="small"
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography sx={labelSx}>
                <FormattedMessage id="devise.signup.phone" />
              </Typography>
              <PhoneInput
                country="be"
                onlyCountries={Object.values(countryMap)}
                value={profileValues.phone}
                onChange={handlePhoneChange}
                placeholder="+32 490 12 34 56"
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  fontSize: "13px",
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  borderRadius: "10px",
                  border: "1px solid hsla(160, 10%, 90%, 0.6)",
                  backgroundColor: "hsla(160, 10%, 96%, 0.3)",
                }}
                buttonStyle={{
                  borderRadius: "10px 0 0 10px",
                  border: "1px solid hsla(160, 10%, 90%, 0.6)",
                  backgroundColor: "hsla(160, 10%, 96%, 0.3)",
                }}
              />
            </Box>
          </Box>

          <Box
            component="button"
            disabled={profileLoading}
            onClick={saveProfile}
            sx={btnPrimarySx}
          >
            {profileLoading ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : null}
            <FormattedMessage id="save" />
          </Box>
        </Box>

        {/* Notifications */}
        <Box sx={{ ...cardSx, p: { xs: "20px", md: "28px" }, mb: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "20px" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: "hsla(153, 68%, 25%, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Bell size={18} color="hsl(153, 68%, 25%)" />
            </Box>
            <Box>
              <Typography sx={sectionTitleSx}>
                <FormattedMessage id="settings.update_notifications" />
              </Typography>
              <Typography sx={sectionDescSx}>
                Choisissez les emails que vous souhaitez recevoir.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "20px" }}>
            <ToggleRow
              label={intl.formatMessage({ id: "notification_preferences.regional_job" })}
              checked={!!notifications?.regional_job}
              onChange={() => toggleNotif("regional_job")}
            />
            <ToggleRow
              label={intl.formatMessage({ id: "notification_preferences.monthly_suggestion" })}
              checked={!!notifications?.monthly_suggestion}
              onChange={() => toggleNotif("monthly_suggestion")}
            />
            <ToggleRow
              label={intl.formatMessage({ id: "notification_preferences.tips_and_tricks" })}
              checked={!!notifications?.tips_and_tricks}
              onChange={() => toggleNotif("tips_and_tricks")}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box
              component="button"
              disabled={notifLoading}
              onClick={() => saveNotifications()}
              sx={btnPrimarySx}
            >
              {notifLoading ? (
                <CircularProgress size={14} sx={{ color: "inherit" }} />
              ) : null}
              <FormattedMessage id="save" />
            </Box>
            <Box
              component="button"
              onClick={() => unsubscribeAll()}
              sx={btnDangerSx}
            >
              <BellOff size={13} />
              <FormattedMessage id="notification_preferences.unsubscribe_all" />
            </Box>
          </Box>
        </Box>

        {/* Privacy */}
        <Box sx={{ ...cardSx, p: { xs: "20px", md: "28px" }, mb: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "20px" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: "hsla(153, 68%, 25%, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Shield size={18} color="hsl(153, 68%, 25%)" />
            </Box>
            <Box>
              <Typography sx={sectionTitleSx}>
                <FormattedMessage id="settings.privacy" />
              </Typography>
              <Typography sx={sectionDescSx}>
                <FormattedMessage id="settings.company_hidden" />
              </Typography>
            </Box>
          </Box>

          <Typography sx={labelSx}>
            <FormattedMessage id="settings.blacklisted_companies" />
          </Typography>
          <Autocomplete
            multiple
            options={companies}
            value={blacklistedCompanies}
            onChange={(_, newVal) => setBlacklistedCompanies(newVal)}
            disableCloseOnSelect
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <Typography sx={{ fontSize: "13px", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {option.name}
                </Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={intl.formatMessage({ id: "settings.search_for_companies" })}
                sx={inputSx}
                size="small"
              />
            )}
            sx={{ mb: "20px" }}
          />

          <Box
            component="button"
            disabled={privacyLoading}
            onClick={savePrivacy}
            sx={btnPrimarySx}
          >
            {privacyLoading ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : null}
            <FormattedMessage id="save" />
          </Box>
        </Box>

        {/* Password */}
        <Box
          component="form"
          onSubmit={handlePasswordSubmit}
          sx={{ ...cardSx, p: { xs: "20px", md: "28px" }, mb: "16px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "20px" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: "hsla(153, 68%, 25%, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Lock size={18} color="hsl(153, 68%, 25%)" />
            </Box>
            <Box>
              <Typography sx={sectionTitleSx}>
                <FormattedMessage id="settings.change_password" />
              </Typography>
              <Typography sx={sectionDescSx}>
                Mettez à jour votre mot de passe de connexion.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: "14px",
              mb: "20px",
            }}
          >
            {!currentCandidate?.password_generated && (
              <Box>
                <Typography sx={labelSx}>
                  <FormattedMessage id="devise.password.current_password" />
                </Typography>
                <TextField
                  name="currentPassword"
                  type={showCurrentPw ? "text" : "password"}
                  value={passwordValues.currentPassword}
                  onChange={handlePwInput}
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword || ""}
                  fullWidth
                  size="small"
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setShowCurrentPw(!showCurrentPw)}
                        sx={{
                          border: "none",
                          bgcolor: "transparent",
                          cursor: "pointer",
                          display: "flex",
                          p: 0,
                          color: "hsl(178, 10%, 45%)",
                        }}
                      >
                        {showCurrentPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </Box>
                    ),
                  }}
                />
              </Box>
            )}
            <Box>
              <Typography sx={labelSx}>
                <FormattedMessage id="devise.password.new_password" />
              </Typography>
              <TextField
                name="password"
                type={showNewPw ? "text" : "password"}
                value={passwordValues.password}
                onChange={handlePwInput}
                error={!!passwordErrors.password}
                helperText={passwordErrors.password || ""}
                fullWidth
                size="small"
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      sx={{
                        border: "none",
                        bgcolor: "transparent",
                        cursor: "pointer",
                        display: "flex",
                        p: 0,
                        color: "hsl(178, 10%, 45%)",
                      }}
                    >
                      {showNewPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </Box>
                  ),
                }}
              />
            </Box>
          </Box>

          <Box
            component="button"
            type="submit"
            disabled={passwordLoading}
            sx={btnPrimarySx}
          >
            {passwordLoading ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : null}
            <FormattedMessage id="save" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
